import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { track, events } from "../../lib/analytics";
import { recordClick } from "../../lib/api";
import { routeIntake } from "../../lib/partnerRouting";
import {
  loadIntakeDraft,
  saveIntakeDraft,
  clearIntakeDraft,
} from "../../lib/intakeStorage";
import { useQuoteModal } from "../../lib/quoteModal";
import { recordFunnelEvent } from "../../lib/funnelEvent";
import { recordFieldBlur, recordFieldFocus, recordFormSubmit } from "../../lib/funnelFieldEvent";
import { fireAudienceFunnelAbandoned } from "../../lib/audienceSeeds";
import IntakeSuccessCard from "./IntakeSuccessCard";
import FunnelProgress from "../funnel/FunnelProgress";
import MatchingState from "../funnel/MatchingState";
import AdPill from "../AdPill";

/**
 * Hook factory for every intake form.
 *
 * Returns shared state + helpers so each modal (Insurance / Auto Loan /
 * Warranty) only owns its UI markup. The shell owns:
 *   - draft hydration from localStorage on mount
 *   - draft auto-save on every change (debounced via the next render)
 *   - submit pipeline (track → recordClick → routeIntake)
 *   - success-card swap on resolved result
 *   - reset path back to the form
 *
 * Each intake calls `useIntake({ category, validate, contextLine })` and
 * receives `{ values, setField, isSubmitting, result, submit, reset }`.
 */
export function useIntake({ category, defaults, validate, contextLine }) {
  const [values, setValues] = useState(() => ({
    ...defaults,
    ...(loadIntakeDraft(category) || {}),
  }));
  const [isSubmitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Iter-166 — Brief "matching providers…" transition between submit
  // success + the success card flip. Dwells for ~1.5 sec so the funnel
  // feels intentional rather than instant.
  const [matching, setMatching] = useState(false);
  // Iter-138 — read funnel attribution from the modal context. When the
  // open() call carried `successContext: { funnel_cluster, funnel_slug }`
  // (e.g. the iter-137 cluster-monetization footer), we tag the
  // server-side `modal_submit_attributed` event with the same cluster +
  // slug so the admin attribution rollup can close the loop.
  const quoteModal = useQuoteModal();

  // GA4: each intake view is a top-of-funnel event.
  useEffect(() => {
    track(events.ZIP_FORM_VIEW, { location: `intake:${category}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist draft on every change. We don't debounce — localStorage writes
  // are sub-millisecond and a category's draft is tiny (<256 bytes).
  useEffect(() => {
    if (!result) saveIntakeDraft(category, values);
  }, [values, category, result]);

  const setField = (k, v) => {
    setValues((prev) => ({ ...prev, [k]: v }));
    // Iter-185.BM (AUDIT #10) — Fire the PostHog "funnel_abandoned"
    // audience anchor on the first real field interaction. Anchor +
    // sessionStorage de-dupe live in audienceSeeds.js so subsequent
    // setField calls within the same session are no-ops.
    fireAudienceFunnelAbandoned({
      category,
      surface: `intake_${category}`,
    });
    // Iter-185.BM (AUDIT #2) — Field-level drop-off telemetry. Records
    // a focus event once per (session, field). Backend de-dupes per
    // session so the dashboard reads % of sessions that interacted with
    // each field, not raw keystroke counts.
    fieldFocus(k);
    // Also fire a blur with completed=true if the field now has a value.
    // This gives the dashboard a "interacted but cleared" vs.
    // "interacted and saved" distinction.
    if (v !== null && v !== undefined && v !== "") {
      fieldBlur(k, v);
    }
  };

  // Iter-185.BM (AUDIT #2) — Field-level drop-off helpers. Each unique
  // (session, surface, field) interaction is recorded ONCE per session
  // so the dashboard can compute "of sessions that started the form,
  // what % engaged each field, what % submitted". The dedupe is
  // intentional — without it, every keystroke would inflate the count.
  const surface = `intake_${category}`;
  // useRef so adding to the set never triggers a re-render.
  const interactedFieldsRef = useRef(new Set());

  const fieldFocus = (field) => {
    if (!field || interactedFieldsRef.current.has(field)) return;
    interactedFieldsRef.current.add(field);
    recordFieldFocus({ surface, category, field, step: 0 });
  };
  const fieldBlur = (field, value) => {
    recordFieldBlur({ surface, category, field, step: 0, value });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    // Iter-185.BM (AUDIT #2) — Record the form submit attempt in the
    // field-level drop-off telemetry. Fires before validation so we
    // capture intent-to-submit even if validation rejects.
    recordFormSubmit({ surface, category, step: 0 });
    const err = validate?.(values);
    if (err) {
      track(events.ZIP_FORM_ERROR, { category, reason: err });
      toast.error(err);
      return;
    }
    setSubmitting(true);
    track(events.ZIP_FORM_SUBMIT, {
      category,
      zip_prefix: values.zip_code ? String(values.zip_code).slice(0, 3) : null,
      intake_field_count: Object.keys(values).length,
    });
    recordClick({
      target: `intake:${category}`,
      source_page:
        typeof window !== "undefined" ? window.location.pathname : "/",
      referrer:
        typeof document !== "undefined" ? document.referrer || null : null,
    });

    const routed = await routeIntake({ category, intake: values });

    if (routed.status === "error") {
      toast.error("Something went wrong — your details are saved. Try again?");
    } else {
      track(events.ZIP_FORM_SUCCESS, {
        category,
        quote_id: routed.quote_id,
        routing_strategy: routed.routing_strategy,
        result_status: routed.status,
      });
      clearIntakeDraft(category);
      // Iter-138 — server-side funnel attribution closes the loop. We
      // only fire when the modal was opened from a known cluster surface
      // (funnel_cluster + funnel_slug present in successContext).
      const ctx = quoteModal?.successContext;
      if (ctx?.funnel_cluster && ctx?.funnel_slug) {
        recordFunnelEvent(
          "modal_submit_attributed",
          ctx.funnel_cluster,
          ctx.funnel_slug,
          {
            category,
            funnel_source: ctx.funnel_source || null,
            quote_id: routed.quote_id || null,
          },
        );
      }
    }

    setResult(null);
    // Iter-167 — Show the matching state for ~2 sec per the spec, THEN
    // flip to the success card. We've already awaited the API result
    // above, so the dwell is purely UX polish, never a fake delay over
    // a real error.
    setMatching(true);
    setTimeout(() => {
      setMatching(false);
      setResult(routed);
      setSubmitting(false);
    }, 2000);
  };

  const reset = () => {
    setResult(null);
    setMatching(false);
    setValues({ ...defaults });
  };

  return { values, setField, isSubmitting, result, matching, submit, reset, contextLine, fieldFocus, fieldBlur };
}

/**
 * Outer presentational wrapper — renders the form children OR the success
 * card based on whether a routing result has resolved. Each intake supplies
 * the form children below and the submit button label.
 */
export function IntakeShell({
  category,
  state,
  submitLabel,
  trustLine = "Free · No hard credit pull · No spam",
  children,
}) {
  const { result, matching, reset, values, isSubmitting, submit, contextLine } = state;

  // Iter-166 — Render the matching transition state between submit
  // success + the success card flip.
  if (matching) {
    return <MatchingState compact />;
  }

  if (result) {
    return (
      <IntakeSuccessCard
        result={result}
        category={category}
        zip={values.zip_code}
        onReset={reset}
        contextLine={contextLine}
      />
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`intake-form-${category}`}
      aria-label={`Quote intake — ${category}`}
      className="space-y-6"
    >
      {/* Iter-166 — Unified 3-step funnel progress at the top of every
          intake form. Matches the homepage hero form + every success
          card so the journey markers are consistent across surfaces.
          Iter-167 — Added bottom margin to provide ~20-24px breathing
          room between the progress indicator and the first form field
          per the funnel-spec target spacing band. */}
      <div className="pb-2">
        <FunnelProgress step={1} state="active" compact />
      </div>
      {children}
      <div className="pt-1">
        {/* Iter-181-A — FTC-compliant disclosure rendered ABOVE the
            monetized CTA on the same viewport. Same Ad·Sponsored pill
            used by the AffiliateRedirect interstitial. */}
        <AdPill
          testid={`intake-ad-pill-${category}`}
          className="mb-2 text-left"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          data-testid={`intake-submit-${category}`}
          className="w-full h-14 bg-slate-950 hover:bg-slate-800 text-white font-bold transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 group disabled:opacity-70"
        >
          {isSubmitting ? "Submitting…" : submitLabel}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
        <p
          data-testid={`intake-trust-${category}`}
          className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 text-center"
        >
          {trustLine}
        </p>
      </div>
    </form>
  );
}
